import {useMutation, UseMutationResult} from '@tanstack/react-query';
import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react';

import {usePrevious, useTimeout} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useEventHandler} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {postApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {DataEventTrackingProvider} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {getHasAdminSetupGuide} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {createSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SandboxScenariosEnum, StepSlug} from '../../../../helpers/sandbox/sandboxScenarioHelpers';
import {SandboxModel} from '../../../../models/api/conversationSandboxModel';
import {ApiSandboxStepDataGroup} from '../../../../models/api/dataGroupModel';
import {makeGetConversationById} from '../../../../store/data/conversations/conversationsSelectors';
import {dataUpdatedAction} from '../../../../store/data/dataGroupsActions';
import {navigateToFirstTeamInboxAction} from '../../../../store/data/teammates/teammatesActions';
import {useOnboardingModalState} from '../../../app/topBar/onboarding/onboardingContext';
import {useOnboardingFlow} from '../../flow/onboardingFlowHelpers';
import {OnboardingFlowTransitionEnum} from '../../flow/onboardingFlowTypes';
import {
  SandboxEducationTipEndClickHandler,
  SandboxEducationTipNextClickHandler,
  SandboxEducationTipProps,
} from './sandboxEducationTip';

/*
 * Props.
 */

interface SandboxEducationTipCoordinatorProps<T extends SandboxScenariosEnum> {
  anchor?: Anchor;
  className?: string;
  conversationId: number | undefined;
  scenario: T;
  step: StepSlug<T>;
  /** Whether the tip is eligible to be displayed based on criteria beyond the scenario and step. */
  isEligible: boolean;
  renderEducationTip: (props: SandboxEducationTipProps) => ReactNode;
  delay?: number;
  /** Handler called when the user wants to end the demo. */
  onEnd?: () => void;
  /** Handler called when the user wants to continue the demo, BEFORE the scenario is advanced to the next step. */
  onBeforeNext?: () => Promise<void> | void;
  /** Handler called when the user wants to continue the demo, AFTER the scenario is advanced to the next step. */
  onNext?: () => void;
  children?: ReactNode;
}

/*
 * Component.
 */

/** It's recommended to render this component unconditionally for consistent layout. */
export const SandboxEducationTipCoordinator = <T extends SandboxScenariosEnum>(
  props: SandboxEducationTipCoordinatorProps<T>,
) => {
  const {className, conversationId, scenario, step, renderEducationTip, delay = 0, children} = props;
  const builtInAnchorRef = useRef<HTMLDivElement>(null);
  const anchor = props.anchor ?? builtInAnchorRef;

  const [isTipVisible, setIsTipVisible] = useState(false);
  const [safeSetTimeout] = useTimeout();

  const [stepIncrementMutation, conversationSandbox] = useSandboxStepIncrement(conversationId);

  const onNextClick: SandboxEducationTipNextClickHandler = async () => {
    await props.onBeforeNext?.();
    stepIncrementMutation.mutate({step});
    props.onNext?.();
  };

  const onEndClick: SandboxEducationTipEndClickHandler = (_event, {shouldAdvanceStep}) => {
    if (shouldAdvanceStep) {
      stepIncrementMutation.mutate({step});
    }
    props.onEnd?.();
  };

  const isVisible =
    !stepIncrementMutation.isLoading &&
    conversationSandbox?.scenario === scenario &&
    conversationSandbox?.step === step &&
    props.isEligible &&
    !conversationSandbox?.completed;
  const wasVisible = usePrevious(isVisible) ?? false;

  useEffect(() => {
    if (isVisible === wasVisible) {
      return;
    }

    safeSetTimeout(
      () => {
        setIsTipVisible(isVisible);
      },
      isVisible ? delay : 0,
    );
  });

  return (
    <>
      {anchor === builtInAnchorRef ? (
        <div className={className} ref={builtInAnchorRef}>
          {children}
        </div>
      ) : (
        children
      )}

      {isTipVisible ? (
        <DataEventTrackingProvider context={{name: 'getting_started', payload: {}}}>
          {renderEducationTip({
            anchor,
            onNextClick,
            onEndClick,
            isVisible: true,
          })}
        </DataEventTrackingProvider>
      ) : null}
    </>
  );
};

/*
 * Hooks.
 */

export function useDismissSandboxEducationTip<T extends SandboxScenariosEnum>(
  conversationId: number | undefined,
  step: StepSlug<T>,
): () => void {
  const [incrementStep] = useSandboxStepIncrement<T>(conversationId);

  return useEventHandler(() => {
    incrementStep.mutate({step});
  });
}

interface StepIncrement<T extends SandboxScenariosEnum> {
  step: StepSlug<T>;
}

type SandboxProgress<T extends SandboxScenariosEnum> = [
  UseMutationResult<void, unknown, StepIncrement<T>, unknown>,
  SandboxModel | undefined,
];

export function useSandboxStepIncrement<T extends SandboxScenariosEnum>(
  conversationId: number | undefined,
): SandboxProgress<T> {
  const getConversation = useMemo(() => {
    if (!conversationId) {
      return () => undefined;
    }

    return makeGetConversationById(conversationId);
  }, [conversationId]);

  const getConversationUrl = useMemo(
    () => createSelector([getConversation], (conversation) => conversation?.url),
    [getConversation],
  );
  const conversationUrl = useAppSelector(getConversationUrl);
  const dispatch = useAppDispatch();

  const getConversationSandbox = useMemo(
    () => createSelector([getConversation], (conversation) => conversation?.sandbox),
    [getConversation],
  );
  const conversationSandbox = useAppSelector(getConversationSandbox);

  const stepIncrementMutation = useMutation(async (currentStep: StepIncrement<T>) => {
    if (!conversationUrl || !conversationSandbox) {
      return;
    }

    const dataGroup = await postApiAsync(ApiSandboxStepDataGroup, `${conversationUrl}/sandbox`, currentStep);
    dispatch(dataUpdatedAction(dataGroup));
  });

  return [stepIncrementMutation, conversationSandbox];
}

export function useSandboxEndDemoHandler() {
  const dispatch = useAppDispatch();
  const {openAdminSetupGuideSetupScreen} = useOnboardingModalState();
  const {navigateOnboardingFlow} = useOnboardingFlow();
  const hasAdminSetupGuide = useAppSelector(getHasAdminSetupGuide);

  return useEventHandler(async () => {
    const isInOnboardingFlow = await navigateOnboardingFlow(
      OnboardingFlowTransitionEnum.SANDBOX_DEMO_COMPLETE,
    );

    if (isInOnboardingFlow) {
      return;
    }

    if (hasAdminSetupGuide) {
      openAdminSetupGuideSetupScreen();
      dispatch(navigateToFirstTeamInboxAction());
      return;
    }

    dispatch(navigateToFirstTeamInboxAction());
  });
}
